import type { ComponentConfigV2, WarmupPolicy, WarmupPolicyMode } from '@/types/configurationV2'

/**
 * 생각열기 회차별 정책 — superadmin 편집기용 순수 헬퍼.
 * 백엔드 `WarmupPolicyResolver` 와 같은 해석 규칙을 미리보기/배지에 재현한다.
 * Spec: docs/superpowers/specs/2026-09-09-lc-review-warmup-policy-design.md §3
 */

export type WarmupRound = 'initial' | 'designatedReview'

export interface ResolvedWarmupPolicy {
  mode: WarmupPolicyMode
  /** warmupPolicy 가 명시돼 있어 그 값을 그대로 쓴 경우. false 면 기존 설정 해석. */
  explicit: boolean
}

export const WARMUP_POLICY_MODES: WarmupPolicyMode[] = ['hidden', 'required', 'optional']

export const WARMUP_POLICY_MODE_LABELS: Record<WarmupPolicyMode, string> = {
  hidden: '미제공',
  required: '필수',
  optional: '선택',
}

export function isWarmupComponent(component: Pick<ComponentConfigV2, 'type'>): boolean {
  return component.type === 'warmup'
}

/**
 * 기존 필드 호환 해석(정책 없음):
 * - enabled=false → 두 회차 모두 hidden
 * - initial → required (단계는 항상 통과해야 했고 녹음만 옵션)
 * - designatedReview → firstAttemptOnly ? hidden : required
 * 명시 정책이 있으면 enabled=false 만 최상위 비활성으로 두고 회차별 값을 그대로 쓴다(복습에서 firstAttemptOnly 보다 우선).
 */
export function resolveWarmupPolicy(
  component: Pick<ComponentConfigV2, 'enabled' | 'firstAttemptOnly' | 'warmupPolicy'>,
  round: WarmupRound,
): ResolvedWarmupPolicy {
  if (!component.enabled) return { mode: 'hidden', explicit: component.warmupPolicy != null }
  if (component.warmupPolicy) {
    return { mode: component.warmupPolicy[round], explicit: true }
  }
  if (round === 'initial') return { mode: 'required', explicit: false }
  return { mode: component.firstAttemptOnly ? 'hidden' : 'required', explicit: false }
}

/**
 * 정책 입력 활성화: initial 은 기존 제공 상태(필수)로 채우고 지정 복습은 운영자가 바꾸기 쉽도록 선택으로 시작한다.
 * firstAttemptOnly·audioResponse 는 초기화하지 않는다(불변 — 새 객체 반환).
 */
export function enableWarmupPolicy(component: ComponentConfigV2): ComponentConfigV2 {
  if (component.warmupPolicy) return component
  // 기존 첫 학습 제공 상태는 항상 required 였다(enabled=false 는 최상위 비활성으로 별도 처리).
  const policy: WarmupPolicy = { initial: 'required', designatedReview: 'optional' }
  return { ...component, warmupPolicy: policy }
}

/** 정책 해제: 필드를 제거해 기존 설정 해석으로 되돌린다(불변). */
export function clearWarmupPolicy(component: ComponentConfigV2): ComponentConfigV2 {
  if (!component.warmupPolicy) return component
  const { warmupPolicy: _removed, ...rest } = component
  return rest as ComponentConfigV2
}

export function setWarmupPolicyMode(
  component: ComponentConfigV2,
  round: WarmupRound,
  mode: WarmupPolicyMode,
): ComponentConfigV2 {
  const base = component.warmupPolicy ?? enableWarmupPolicy(component).warmupPolicy!
  return { ...component, warmupPolicy: { ...base, [round]: mode } }
}

// ---- 학습 흐름 미리보기 -------------------------------------------------------------

export type PreviewState = 'hidden' | 'required' | 'optional'

export interface PreviewEntry {
  id: string
  label: string
  type: ComponentConfigV2['type']
  state: PreviewState
  /** 레슨/콘텐츠 조건에 따라 달라질 수 있음을 알리는 부가 설명 */
  note?: string
}

function isPreviousScope(component: ComponentConfigV2): boolean {
  const options = component.options as Record<string, unknown> | undefined
  if (!options) return false
  if (component.type === 'video') {
    const source = options.source as { contentScope?: string } | undefined
    return source?.contentScope === 'previous'
  }
  if (component.type === 'quiz') {
    const extraction = options.extraction as { contentScope?: string | string[] } | undefined
    const scope = extraction?.contentScope
    if (Array.isArray(scope)) return scope.some((v) => String(v).includes('previous'))
    return typeof scope === 'string' && scope.includes('previous')
  }
  return false
}

function isWrongInReview(component: ComponentConfigV2): boolean {
  if (component.type !== 'quiz') return false
  const extraction = (component.options as { extraction?: { answerFilter?: string } } | undefined)?.extraction
  return extraction?.answerFilter === 'wrong_in_review'
}

/**
 * 선택한 분기(기본/override)의 컴포넌트 목록이 각 회차에서 어떻게 보이는지 계산한다.
 * 백엔드 `filterLessonComponentsForFlow` + `WarmupPolicyResolver` 의 제공 규칙을 재현하되,
 * 특정 레슨을 모르므로 콘텐츠 유무·직전 레슨 유무 조건은 note 로만 표시한다. 실제 재생·학습자 ID 노출은 없다.
 */
export function buildFlowPreview(components: ComponentConfigV2[], round: WarmupRound): PreviewEntry[] {
  return [...components]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((component) => {
      const label = component.label || component.type
      const base = { id: component.id, label, type: component.type }
      if (!component.enabled) return { ...base, state: 'hidden' as const, note: '비활성' }
      if (isWarmupComponent(component)) {
        const resolved = resolveWarmupPolicy(component, round)
        return {
          ...base,
          state: resolved.mode,
          note: resolved.explicit ? undefined : '기존 설정 해석',
        }
      }
      if (round === 'designatedReview') {
        if (component.firstAttemptOnly) return { ...base, state: 'hidden' as const, note: '1회차에만 제공' }
        if (isPreviousScope(component)) return { ...base, state: 'hidden' as const, note: '복습에서는 직전 레슨 범위 제외' }
      } else {
        if (isWrongInReview(component)) return { ...base, state: 'hidden' as const, note: '복습 전용(오답 풀기)' }
        if (isPreviousScope(component)) return { ...base, state: 'required' as const, note: '직전 레슨이 있을 때만' }
      }
      if (component.type === 'interactive' || component.type === 'buildup') {
        return { ...base, state: 'required' as const, note: '레슨 콘텐츠가 있을 때만' }
      }
      return { ...base, state: 'required' as const }
    })
}
